/**
 * The workflow element contract, types only: what the editor knows about one kind of step (an action or a flow-control step).
 * An element is plain data. Its `fields` ARE Surface Engine fields bound into the step, so the step panel, the picker entry, the canvas node,
 * the binding types and the validator's action inputs all derive from the one constant in `workflow.elements/<type>.element.ts`.
 * Invariant: an action element's binds are `input.<name>`, and those names are the runtime adapter's input names; renaming one breaks saved definitions.
 */
import type { ActivityEntityName } from '../shared/activity.entity.name';
import type { JsonSafeMetadata } from '../surface.engine/json.safe.metadata.zod';
import type { WorkflowActionType, WorkflowStepType } from './workflow.definition';
import type { WorkflowEntityOperation } from './workflow.entity.operation';

/** Where an Enum field's choices come from when neither the element nor an entity's metadata holds them; the editor fills them. */
export enum WorkflowFieldOptionSource {
    /** The definition's declared variables, by name. */
    Variables = 'variables',
    /**
     * The published email merge templates a Job may send, by id: `GET /api/v2/public/jobs/:jobId/activities/templates?channel=email`.
     * Not form templates (`ActivityEntityName.FormTemplate`): the email endpoint resolves `templateId` among merge templates.
     */
    EmailTemplates = 'emailTemplates',
    /** The published SMS merge templates a Job may send, by id: `GET /api/v2/public/jobs/:jobId/activities/templates?channel=sms`. */
    SmsTemplates = 'smsTemplates',
}

/** One element per action and one per flow-control step kind; `WorkflowStepType.Action` itself is not an element, its actions are. */
export type WorkflowElementType = WorkflowActionType | Exclude<WorkflowStepType, WorkflowStepType.Action>;

/** The picker group an element sits in. */
export enum WorkflowFamily {
    Flow = 'flow',
    Job = 'job',
    Stage = 'stage',
    Communication = 'communication',
}

/**
 * What an element changes outside the workflow walk when it runs; determines how `test-step` and `test-workflow` execute it
 * (`none` and `read` run for real; `time` simulates advancing the clock; `write`, `trigger-visible`, `send` and `external`
 * return a preview unless `test-step` is called in `live` mode).
 */
export enum WorkflowSideEffect {
    None = 'none',
    Read = 'read',
    Time = 'time',
    Write = 'write',
    TriggerVisible = 'trigger-visible',
    Send = 'send',
    External = 'external',
}

/** The workflow-specific editor a field needs instead of the native one inferred from its metadata. */
export enum WorkflowFieldRenderer {
    /** The all/any/not condition builder over comparisons. */
    Condition = 'condition',
    /** Merge HTML text with workflow tags, and record tags where the field declares `recordTags`. */
    MergeText = 'mergeText',
    /** A picker over trigger, step-output and loop-item paths. */
    Binding = 'binding',
    /** `<count> <unit>` in minutes, hours or days. */
    Duration = 'duration',
}

/** One kind of step the editor offers: how it is listed, what its panel edits, what later steps can read from it and which child lists it lays out. */
export interface WorkflowElement {
    type: WorkflowElementType;
    family: WorkflowFamily;
    /** What running this element does to tenant state or external channels. */
    sideEffect: WorkflowSideEffect;
    /** The picker and node label. */
    title: string;
    /** A Font Awesome icon name, such as `fa-note-sticky`. */
    icon: string;
    fields: WorkflowElementField[];
    /** What later steps can bind to as `steps.<id>.<path>`; empty for flow-control steps. */
    outputs: WorkflowElementOutput[];
    /** The child step lists, in the order the canvas lays them out; absent for an element without children. */
    branches?: WorkflowElementBranch[];
    /**
     * The run entity API operation this element performs (`/api/extensions/runs/:runId/entities/:entity/...`); absent for an
     * element that performs none. A workflow run may perform only the entity operations its frozen definition names with literal
     * values (`collectWorkflowEntityOperations`), so leaving it off a new record element makes that element's calls answer 403.
     */
    entityOperation?: WorkflowElementEntityOperation;
}

/**
 * What an element does through the run entity API. The entity is either fixed here or named by the step's `WORKFLOW_ENTITY_INPUT`
 * input; an `Action` names its action id in the `WORKFLOW_ENTITY_ACTION_INPUT` input (`workflow.entity.operations.collect.ts`).
 */
export interface WorkflowElementEntityOperation {
    operation: WorkflowEntityOperation;
    /** The entity every step of this element acts on; absent when the step's `entity` input names it. */
    entity?: ActivityEntityName;
}

/** One panel field: a Surface Engine field whose `bind` is a path into the step, either described inline or read from an entity's canonical metadata. */
export type WorkflowElementField = WorkflowInlineField | WorkflowEntityField;

/** What every element field carries. */
interface WorkflowElementFieldBase {
    /** A path into the step, which is the form model: `input.<name>` for an action input, or a step prop such as `condition`. */
    bind: string;
    required?: boolean;
    renderer?: WorkflowFieldRenderer;
    /** Whether the value may be a binding rather than a literal; absent means true for an action input. */
    bindable?: boolean;
    /**
     * On a merge-text field: its text may hold record merge tags, which WorkBuddy renders against the run's record when the action
     * sends it. Absent means the run reads the text itself, so the validator refuses a record tag there.
     */
    recordTags?: boolean;
    /** Set on an Enum field whose choices the editor fills from the definition being edited, such as its variables. */
    optionSource?: WorkflowFieldOptionSource;
    /**
     * The bind of a sibling field naming a variable; the editor types this field as that variable is declared, so a number
     * variable's value opens a number editor. The element's own metadata is the type until a variable is chosen.
     */
    typeOf?: string;
}

/** A field with no entity counterpart, described in full here. */
export interface WorkflowInlineField extends WorkflowElementFieldBase {
    metadata: JsonSafeMetadata;
}

/**
 * A real entity field (`jms-api/workbuddy/metadata`) a workflow value is typed from. The editor reads that field's canonical
 * uiFields metadata, so the value gets the field's own editor, picker (its entity data strategy) and display, as the boards do.
 */
export interface WorkflowEntityFieldRef {
    entity: ActivityEntityName;
    /** The entity's uiFields key. */
    field: string;
}

/** A field that edits a real entity field; its type and editor come from that field's canonical uiFields metadata. */
export interface WorkflowEntityField extends WorkflowElementFieldBase, WorkflowEntityFieldRef {
    /** Presentation overrides layered over the canonical metadata, such as a shorter label. */
    metadata?: JsonSafeMetadata;
}

/** One value an action returns, readable by later steps at `steps.<id>.<path>`. */
export interface WorkflowElementOutput {
    path: string;
    /** The value's type as the validator reads it; the editor replaces it with `entityField`'s metadata when that is set. */
    metadata: JsonSafeMetadata;
    /** The entity field this output holds, so the editor types it from that field's metadata; absent for a value no entity field holds. */
    entityField?: WorkflowEntityFieldRef;
    /** On a list output: what the binding picker calls one item inside a For each over it, such as "This stage". */
    itemTitle?: string;
}

/**
 * One child step list of a flow-control element. `then`, `else`, `default` and `steps` hold a step list directly;
 * `cases` holds a list of switch cases, each with its own `equals` and `steps`.
 */
export interface WorkflowElementBranch {
    prop: 'then' | 'else' | 'cases' | 'default' | 'steps';
    title: string;
}
